import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';

const NotificationTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, templateId: null, action: '' });
  const [formData, setFormData] = useState({
    name: '',
    type: 'engagement',
    title: '',
    message: '',
    is_active: true,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getTemplates();
      if (response.data.success) {
        setTemplates(response.data.data.templates);
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to fetch templates', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.deleteTemplate(id);
      setToast({ show: true, message: 'Template deleted successfully', type: 'success' });
      fetchTemplates();
      setConfirmModal({ show: false, templateId: null, action: '' });
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to delete', type: 'error' });
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      title: template.title,
      message: template.message,
      is_active: template.is_active,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await notificationsAPI.updateTemplate(editingTemplate.id, formData);
        setToast({ show: true, message: 'Template updated successfully', type: 'success' });
      } else {
        await notificationsAPI.createTemplate(formData);
        setToast({ show: true, message: 'Template created successfully', type: 'success' });
      }
      fetchTemplates();
      resetForm();
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to save', type: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'engagement',
      title: '',
      message: '',
      is_active: true,
    });
    setEditingTemplate(null);
    setShowModal(false);
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Notification Templates</h2>
            <p className="page-subtitle">Reusable notification templates</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-circle me-2"></i>New Template
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : templates.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-envelope-open display-1 text-muted mb-3"></i>
          <h5>No templates yet</h5>
          <p className="text-muted">Create your first notification template to get started.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-circle me-2"></i>Create Template
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {templates.map(template => (
            <div key={template.id} className="col-md-6 col-lg-4">
              <div className="stat-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="mb-0">{template.name}</h6>
                  <span className={`badge ${template.is_active ? 'badge-success' : 'badge-secondary'}`}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-muted small mb-2"><strong>Type:</strong> {template.type}</p>
                <p className="text-muted small mb-2"><strong>Title:</strong> {template.title}</p>
                <p className="text-muted small">{template.message.substring(0, 100)}...</p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleEdit(template)}
                    title="Edit"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setConfirmModal({ show: true, templateId: template.id, action: 'delete' })}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Template Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingTemplate ? 'Edit Template' : 'Create Notification Template'}</h5>
                <button className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Template Name</label>
                    <input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Type</label>
                    <select className="form-select" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                      <option value="engagement">Engagement</option>
                      <option value="prayer">Prayer</option>
                      <option value="habit">Habit</option>
                      <option value="cycle">Cycle</option>
                      <option value="qada">Qada</option>
                      <option value="insight">Insight</option>
                      <option value="spiritual">Spiritual</option>
                      <option value="system">System</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input type="text" className="form-control" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Message</label>
                    <textarea className="form-control" rows="4" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary">{editingTemplate ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, templateId: null, action: '' })}
        onConfirm={() => handleDelete(confirmModal.templateId)}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        type="danger"
      />
    </div>
  );
};

export default NotificationTemplates;